import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Text,
    Icon,
    Stack,
    PrimaryButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Dropdown,
    IDropdownOption,
    SearchBox,
} from '@fluentui/react';
import { CancelToken } from 'axios';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { AuthContext } from '../../../HubLayout/models/AuthContext';
import { DRIItem, DRIItemType } from '../../../Common/models/DRIItem';
import {
    getDRIItems,
    setDRIItem,
    deleteDRIItem,
} from '../../../Common/api/ABHub/DRIItemApi';
import { usePromise } from '../../../Common/hooks/usePromise';
import { DRIItemCard } from './DRIItemCard';
import { itemTypeOptions } from './DRIToolsUtility';
import { defaultIconForType } from './DRIToolsUtility';
import { HubHeader } from '../../../Common/components/HubHeader';

export const DRITools: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();
    const capabilities = useContext(CapabilityContext);
    const { authContext } = useContext(AuthContext);

    const [driItems, setDRIItems] = useState<DRIItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [editingItem, setEditingItem] = useState<DRIItem | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>('');
    const [filterType, setFilterType] = useState<string | null>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        new Set()
    );

    const fetchItems = useCallback(async (cancelToken?: CancelToken) => {
        try {
            setErrorMessage(null);
            const items = await getDRIItems(cancelToken);
            return items;
        } catch (error) {
            setErrorMessage(
                `Failed to load DRI items: ${(error as Error).message}`
            );
            throw error;
        }
    }, []);

    const [list, error, isLoaded, startFetch] = usePromise(fetchItems, true);

    useEffect(() => {
        if (list) {
            setDRIItems(list);
        }
    }, [list]);

    const handleSave = useCallback(
        async (item: DRIItem) => {
            try {
                setErrorMessage(null);
                setSuccessMessage(null);
                const userName =
                    authContext.user?.name || authContext.user?.username || '';

                const toSave: DRIItem = {
                    ...item,
                    lastUpdatedBy: userName,
                    lastUpdatedDate: new Date(),
                };

                if (!toSave.id || toSave.id === 'new-item') {
                    toSave.id = undefined;
                    toSave.createdBy = userName;
                    toSave.createdDate = new Date();
                }

                await setDRIItem(toSave);
                setSuccessMessage(
                    item.id && item.id !== 'new-item'
                        ? 'Item updated successfully'
                        : 'Item created successfully'
                );
                setEditingItem(null);
                startFetch();
            } catch (err) {
                setErrorMessage(
                    `Failed to save item: ${(err as Error).message}`
                );
            }
        },
        [authContext, startFetch]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                setErrorMessage(null);
                setSuccessMessage(null);
                setDeletingId(id);
                await deleteDRIItem(id);
                setSuccessMessage('Item deleted successfully');
                setDeletingId(null);
                startFetch();
            } catch (err) {
                setErrorMessage(
                    `Failed to delete item: ${(err as Error).message}`
                );
                setDeletingId(null);
            }
        },
        [startFetch]
    );

    const handleAddNew = () => {
        const newItem: DRIItem = {
            id: 'new-item',
            title: '',
            itemType: 'link',
            url: '',
            content: '',
            icon: 'Link',
            createdBy: '',
            createdDate: new Date(),
        };
        setEditingItem(newItem);
    };

    const handleDuplicate = useCallback((item: DRIItem) => {
        const duplicated: DRIItem = {
            ...item,
            id: 'new-item',
            title: `${item.title} (Copy)`,
            createdBy: '',
            createdDate: new Date(),
            lastUpdatedBy: undefined,
            lastUpdatedDate: undefined,
        };
        setEditingItem(duplicated);
    }, []);

    const toggleGroupCollapse = (key: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const filteredItems = useMemo(() => {
        let items = driItems;
        if (filterType) {
            items = items.filter((i) => i.itemType === filterType);
        }
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            items = items.filter(
                (i) =>
                    i.title.toLowerCase().includes(q) ||
                    (i.description &&
                        i.description.toLowerCase().includes(q)) ||
                    (i.url && i.url.toLowerCase().includes(q)) ||
                    (i.content && i.content.toLowerCase().includes(q)) ||
                    (i.createdBy && i.createdBy.toLowerCase().includes(q)) ||
                    (i.lastUpdatedBy &&
                        i.lastUpdatedBy.toLowerCase().includes(q))
            );
        }
        return items;
    }, [driItems, searchText, filterType]);

    const filterTypeDropdownOptions: IDropdownOption[] = [
        { key: '', text: 'All Types' },
        ...itemTypeOptions,
    ];

    const hasActiveFilters = searchText.trim() !== '' || filterType !== null;

    if (!capabilities.check(Capability.srmAdminRole)) {
        return null;
    }

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    paddingTop: isMobile ? '16px' : '0',
                    maxWidth: '100%',
                }}
            >
                {/* Top Header Container */}
                <div
                    style={{
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        boxShadow:
                            '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                        overflow: 'hidden',
                        marginBottom: '16px',
                    }}
                >
                    {/* Header */}
                    <HubHeader
                        title="DRI Tools"
                        subtitle="Links, notes, and scripts for sovereign cloud operations"
                        actions={
                            <PrimaryButton
                                text="Add Item"
                                iconProps={{ iconName: 'Add' }}
                                onClick={handleAddNew}
                                disabled={editingItem !== null}
                            />
                        }
                    />

                    {/* Content */}
                    <div style={{ padding: isMobile ? '16px' : '20px' }}>
                        {errorMessage && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                                onDismiss={() => setErrorMessage(null)}
                                dismissButtonAriaLabel="Close"
                                styles={{ root: { marginBottom: '16px' } }}
                            >
                                {errorMessage}
                            </MessageBar>
                        )}
                        {successMessage && (
                            <MessageBar
                                messageBarType={MessageBarType.success}
                                isMultiline={false}
                                onDismiss={() => setSuccessMessage(null)}
                                dismissButtonAriaLabel="Close"
                                styles={{ root: { marginBottom: '16px' } }}
                            >
                                {successMessage}
                            </MessageBar>
                        )}

                        {/* Search and Filter Bar */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '12px',
                                marginBottom: '16px',
                                flexWrap: isMobile ? 'wrap' : 'nowrap',
                            }}
                        >
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <SearchBox
                                    placeholder="Search items by title, description, content..."
                                    value={searchText}
                                    onChange={(_, v) => setSearchText(v || '')}
                                    onClear={() => setSearchText('')}
                                    styles={{ root: { width: '100%' } }}
                                />
                            </div>
                            <Dropdown
                                placeholder="Filter by type"
                                selectedKey={filterType || ''}
                                options={filterTypeDropdownOptions}
                                onChange={(_, option) =>
                                    setFilterType(
                                        option && option.key !== ''
                                            ? (option.key as string)
                                            : null
                                    )
                                }
                                styles={{ root: { minWidth: '160px' } }}
                            />
                            {hasActiveFilters && (
                                <IconButton
                                    iconProps={{ iconName: 'ClearFilter' }}
                                    title="Clear all filters"
                                    ariaLabel="Clear all filters"
                                    onClick={() => {
                                        setSearchText('');
                                        setFilterType(null);
                                    }}
                                />
                            )}
                        </div>

                        {!isLoaded && (
                            <Spinner
                                size={SpinnerSize.large}
                                label="Loading DRI items..."
                            />
                        )}

                        {/* New Item Form */}
                        {editingItem && (
                            <DRIItemCard
                                item={editingItem}
                                theme={theme}
                                isMobile={isMobile}
                                isNew={true}
                                isDeleting={false}
                                onSave={handleSave}
                                onCancel={() => setEditingItem(null)}
                                onDelete={() => {}}
                            />
                        )}

                        {/* Existing Items grouped by type */}
                        {itemTypeOptions
                            .filter((opt) =>
                                filteredItems.some(
                                    (i) => i.itemType === opt.key
                                )
                            )
                            .map((opt) => {
                                const groupKey = opt.key as string;
                                const isCollapsed =
                                    collapsedGroups.has(groupKey);
                                const groupItems = filteredItems.filter(
                                    (i) => i.itemType === opt.key
                                );
                                return (
                                    <div
                                        key={groupKey}
                                        style={{ marginBottom: '20px' }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                marginBottom: isCollapsed
                                                    ? '0'
                                                    : '8px',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}
                                            onClick={() =>
                                                toggleGroupCollapse(groupKey)
                                            }
                                        >
                                            <Icon
                                                iconName={
                                                    isCollapsed
                                                        ? 'ChevronRight'
                                                        : 'ChevronDown'
                                                }
                                                styles={{
                                                    root: {
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .neutralSecondary,
                                                    },
                                                }}
                                            />
                                            <Icon
                                                iconName={defaultIconForType(
                                                    opt.key as DRIItemType
                                                )}
                                                styles={{
                                                    root: {
                                                        fontSize: 16,
                                                        color: theme.palette
                                                            .themePrimary,
                                                    },
                                                }}
                                            />
                                            <Text
                                                variant="mediumPlus"
                                                styles={{
                                                    root: {
                                                        fontWeight: 600,
                                                        color: theme.palette
                                                            .neutralPrimary,
                                                    },
                                                }}
                                            >
                                                {opt.text}
                                            </Text>
                                            <Text
                                                variant="small"
                                                styles={{
                                                    root: {
                                                        color: theme.palette
                                                            .neutralTertiary,
                                                        marginLeft: '4px',
                                                    },
                                                }}
                                            >
                                                ({groupItems.length})
                                            </Text>
                                        </div>
                                        {!isCollapsed && (
                                            <Stack tokens={{ childrenGap: 8 }}>
                                                {groupItems.map((item) => (
                                                    <DRIItemCard
                                                        key={item.id}
                                                        item={item}
                                                        theme={theme}
                                                        isMobile={isMobile}
                                                        isNew={false}
                                                        isDeleting={
                                                            deletingId ===
                                                            item.id
                                                        }
                                                        onSave={handleSave}
                                                        onCancel={() => {}}
                                                        onDelete={() =>
                                                            item.id &&
                                                            handleDelete(
                                                                item.id
                                                            )
                                                        }
                                                        onDuplicate={
                                                            handleDuplicate
                                                        }
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    </div>
                                );
                            })}

                        {isLoaded && driItems.length === 0 && !editingItem && (
                            <Text
                                variant="medium"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        textAlign: 'center',
                                        padding: '32px',
                                    },
                                }}
                            >
                                No DRI items configured. Click &quot;Add
                                Item&quot; to create one.
                            </Text>
                        )}
                        {isLoaded &&
                            driItems.length > 0 &&
                            filteredItems.length === 0 &&
                            hasActiveFilters && (
                                <Text
                                    variant="medium"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            textAlign: 'center',
                                            padding: '32px',
                                        },
                                    }}
                                >
                                    No items match your search or filter.
                                </Text>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
};
